import { useEffect, useMemo } from 'react'
import { useMutation, useQuery, gql, useReactiveVar } from '@apollo/client'
import { uniqBy } from 'lodash'

import { updateSavedFilter } from 'src/reactive-variables'
import {
  SavedFilter,
  savedFiltersVar,
} from 'src/reactive-variables/saved-filters'
import { tryParseJson } from 'src/utils/others'

import {
  AllUserFilterCollections,
  AllUserFilterCollectionsVariables,
  DeleteUserFilterCollection,
  DeleteUserFilterCollectionVariables,
  UpdateOrCreateUserFilterCollection,
  UpdateOrCreateUserFilterCollectionVariables,
} from './__generated__/types'
import { DEFAULT_FILTER_KEY } from './ui/SavedFilterButton'

export type TSavedFiltersData = SavedFilter

export const UPDATE_OR_CREATE_USER_FILTER_COLLECTION = gql`
  mutation UpdateOrCreateUserFilterCollection(
    $filters: JSONString!
    $isDefault: Boolean = false
    $name: String!
    $selfId: ID
    $tableKey: String!
  ) {
    updateOrCreateUserFilterCollection(
      filters: $filters
      isDefault: $isDefault
      name: $name
      selfId: $selfId
      tableKey: $tableKey
    ) {
      userFilterCollection {
        id
        isDefault
        filters
        name
        tableKey
      }
    }
  }
`

export const DELETE_USER_FILTER_COLLECTION = gql`
  mutation DeleteUserFilterCollection($selfId: ID!) {
    deleteUserFilterCollection(selfId: $selfId) {
      success
    }
  }
`

export const SET_DEFAULT_USER_FILTER_COLLECTION = gql`
  mutation SetDefaultUserFilterCollection($selfId: ID!) {
    setDefaultUserFilterCollection(selfId: $selfId) {
      userFilterCollection {
        id
        isDefault
      }
    }
  }
`

export const USER_FILTER_COLLECTION = gql`
  query UserFilterCollection($selfId: ID, $tableKey: String) {
    userFilterCollection(selfId: $selfId, tableKey: $tableKey) {
      id
      name
      filters
      isDefault
      tableKey
    }
  }
`

export const ALL_USER_FILTER_COLLECTIONS = gql`
  query AllUserFilterCollections(
    $tableKey: String
    $name: String
    $isDefault: Boolean
  ) {
    allUserFilterCollections(
      tableKey: $tableKey
      name: $name
      isDefault: $isDefault
    ) {
      edges {
        node {
          id
          name
          filters
          isDefault
          tableKey
        }
      }
    }
  }
`

export const useSavedFiltersApi = (filtersKeyBase: string) => {
  const savedFilters = useReactiveVar(savedFiltersVar)

  // The boot sequence already fetches ALL of the user's filter collections
  // with this exact no-variables operation (src/App/router.tsx). Querying
  // per tableKey here created a different cache entry per table and hit the
  // network once per table on every page. Reuse the boot result (cache-first,
  // no variables) and narrow to this table's key client-side.
  const { data: allCollectionsData, refetch: refetchFilters } = useQuery<
    AllUserFilterCollections,
    AllUserFilterCollectionsVariables
  >(ALL_USER_FILTER_COLLECTIONS, {
    fetchPolicy: 'cache-first',
  })

  const filtersQueryData: AllUserFilterCollections | undefined = useMemo(() => {
    if (!allCollectionsData?.allUserFilterCollections) {
      return allCollectionsData
    }
    return {
      ...allCollectionsData,
      allUserFilterCollections: {
        ...allCollectionsData.allUserFilterCollections,
        edges: (allCollectionsData.allUserFilterCollections.edges || []).filter(
          ({ node }) => node?.tableKey === filtersKeyBase
        ),
      },
    }
  }, [allCollectionsData, filtersKeyBase])

  // onCompleted doesn't fire reliably for cache-served results - mirror the
  // query data into the reactive var whenever it changes instead.
  useEffect(() => {
    if (!filtersQueryData) return
    const filters = (filtersQueryData.allUserFilterCollections?.edges?.map(
      ({ node }) => node
    ) || []) as TSavedFiltersData[]
    const currentFilters = savedFiltersVar() || {}
    savedFiltersVar({ ...currentFilters, [filtersKeyBase]: filters })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersQueryData, filtersKeyBase])

  const [updateOrCreateFilter] = useMutation<
    UpdateOrCreateUserFilterCollection,
    UpdateOrCreateUserFilterCollectionVariables
  >(UPDATE_OR_CREATE_USER_FILTER_COLLECTION)

  const [deleteFilter] = useMutation<
    DeleteUserFilterCollection,
    DeleteUserFilterCollectionVariables
  >(DELETE_USER_FILTER_COLLECTION)

  const filtersData: TSavedFiltersData[] = useMemo(() => {
    const cachedItems =
      savedFilters?.[filtersKeyBase]?.map((item) => ({
        ...item,
        filters: tryParseJson(item!.filters),
      })) || []

    const remoteItems =
      filtersQueryData?.allUserFilterCollections?.edges?.map(({ node }) => ({
        ...node,
        filters: tryParseJson(node!.filters),
      })) || []

    return uniqBy([...cachedItems, ...remoteItems], 'id') as TSavedFiltersData[]
  }, [filtersKeyBase, filtersQueryData, savedFilters])

  const updateFilterHelper = async (
    id: string,
    updates: {
      name?: string
      filters?: Record<string, unknown>
      isDefault?: boolean
    }
  ) => {
    const filter = filtersData.find((f) => f.id === id)
    if (!filter) return

    const response = await updateOrCreateFilter({
      variables: {
        selfId: id,
        name: updates.name ?? filter.name,
        filters: JSON.stringify(updates.filters ?? filter.filters),
        isDefault: updates.isDefault ?? filter.isDefault,
        tableKey: filtersKeyBase,
      },
    })

    const result = response.data?.updateOrCreateUserFilterCollection
    const item = result?.userFilterCollection
    if (item) updateSavedFilter(filtersKeyBase, item.id, item)

    refetchFilters()
  }

  const handleDeleteFilter = async (id: string) => {
    updateSavedFilter(filtersKeyBase, id)
    await deleteFilter({ variables: { selfId: id } })
    refetchFilters()
  }

  const handleRenameFilter = async (id: string, name: string) => {
    await updateFilterHelper(id, { name })
  }

  const handleSetDefault = async (id: string, value: boolean) => {
    const currentFilters = savedFiltersVar() || {}
    const tableFilters = currentFilters[filtersKeyBase] || []

    if (id === DEFAULT_FILTER_KEY) {
      const currentActive = tableFilters.find((item) => item.isDefault)
      const updatedFilters = tableFilters.map((filter) => ({
        ...filter,
        isDefault: false,
      }))
      savedFiltersVar({ ...currentFilters, [filtersKeyBase]: updatedFilters })
      if (currentActive)
        await updateFilterHelper(currentActive.id, { isDefault: false })
    } else {
      const updatedFilters = tableFilters.map((filter) => ({
        ...filter,
        isDefault: filter.id === id ? value : false,
      }))
      savedFiltersVar({ ...currentFilters, [filtersKeyBase]: updatedFilters })
      await updateFilterHelper(id, { isDefault: value })
    }
  }

  const handleUpdateFilter = async (
    id: string,
    filters: Record<string, unknown>
  ) => {
    await updateFilterHelper(id, { filters })
  }

  return {
    filtersData,
    refetchFilters,
    updateOrCreateFilter,
    deleteFilter,
    handleRenameFilter,
    handleSetDefault,
    handleDeleteFilter,
    handleUpdateFilter,
  }
}
